import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { AssessmentVariantMetadata } from "@/lib/api_client";

export interface AdvancedParams {
  k?: number;
  temperature?: number;
  maxTokens?: number;
  model?: string;
  provider?: string;
  promptAddition?: string;
  existingText?: string;
}

interface AdvancedParamsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (params: AdvancedParams) => void;
  defaultMetadata?: AssessmentVariantMetadata | null;
  variantName?: string;
  isReanalysis?: boolean;
  existingText?: string;
}

export function AdvancedParamsDialog({
  open,
  onOpenChange,
  onGenerate,
  defaultMetadata,
  variantName,
  isReanalysis = false,
  existingText = "",
}: AdvancedParamsDialogProps) {
  const [k, setK] = useState("");
  const [temperature, setTemperature] = useState("");
  const [maxTokens, setMaxTokens] = useState("");
  const [model, setModel] = useState("");
  const [provider, setProvider] = useState("");
  const [promptAddition, setPromptAddition] = useState("");
  const [existingTextState, setExistingTextState] = useState("");

  useEffect(() => {
    if (open) {
      setK(defaultMetadata?.k?.toString() ?? "");
      setTemperature(defaultMetadata?.temperature?.toString() ?? "");
      setMaxTokens(defaultMetadata?.maxTokens?.toString() ?? "");
      setModel(defaultMetadata?.model ?? "");
      setProvider(defaultMetadata?.provider ?? "");
      setPromptAddition("");
      setExistingTextState(existingText);
    }
  }, [open, defaultMetadata, existingText]);

  const handleGenerate = () => {
    const params: AdvancedParams = {
      k: k ? parseInt(k) : undefined,
      temperature: temperature ? parseFloat(temperature) : undefined,
      maxTokens: maxTokens ? parseInt(maxTokens) : undefined,
      model: model || undefined,
      provider: provider || undefined,
      promptAddition: promptAddition || undefined,
      existingText: isReanalysis ? existingTextState || undefined : undefined,
    };
    onGenerate(params);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isReanalysis ? "Reanalyser" : "Generer"}{variantName ? ` — ${variantName}` : ""}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-admin-text-primary">K (Top K)</label>
            <Input type="number" value={k} onChange={(e) => setK(e.target.value)} placeholder="f.eks. 40" className="h-8 text-sm mt-1" />
            <p className="text-[10px] text-admin-text-subtle mt-0.5">Sampling parameter for model output diversity</p>
          </div>

          <div>
            <label className="text-xs font-medium text-admin-text-primary">Temperature</label>
            <Input type="number" step="0.1" min="0" max="2" value={temperature} onChange={(e) => setTemperature(e.target.value)} placeholder="f.eks. 0.7" className="h-8 text-sm mt-1" />
            <p className="text-[10px] text-admin-text-subtle mt-0.5">Controls randomness (0 = focused, 2 = creative)</p>
          </div>

          <div>
            <label className="text-xs font-medium text-admin-text-primary">Max Tokens</label>
            <Input type="number" value={maxTokens} onChange={(e) => setMaxTokens(e.target.value)} placeholder="f.eks. 1000" className="h-8 text-sm mt-1" />
            <p className="text-[10px] text-admin-text-subtle mt-0.5">Maximum length of generated response</p>
          </div>

          <div>
            <label className="text-xs font-medium text-admin-text-primary">Model</label>
            <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="f.eks. gpt-4" className="h-8 text-sm mt-1" />
          </div>

          <div>
            <label className="text-xs font-medium text-admin-text-primary">Provider</label>
            <Input value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="f.eks. openai" className="h-8 text-sm mt-1" />
          </div>

          {isReanalysis && (
            <div>
              <label className="text-xs font-medium text-admin-text-primary">Eksisterende tekst</label>
              <Textarea
                value={existingTextState}
                onChange={(e) => setExistingTextState(e.target.value)}
                placeholder="Gjeldende tekst..."
                className="min-h-[200px] text-sm mt-1"
              />
              <p className="text-[10px] text-admin-text-subtle mt-0.5">Rediger eksisterende tekst for regenerering</p>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-admin-text-primary">Tilleggsprompt</label>
            <Textarea
              value={promptAddition}
              onChange={(e) => setPromptAddition(e.target.value)}
              placeholder="Ekstra instruksjoner for AI..."
              className="min-h-[80px] text-sm mt-1"
            />
            <p className="text-[10px] text-admin-text-subtle mt-0.5">Ekstra kontekst eller instruksjoner for denne genereringen</p>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button size="sm" onClick={handleGenerate}>{isReanalysis ? "Reanalyser" : "Generer"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
